import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export default function Thangka() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-28 md:py-36 bg-[#1e1a16] relative overflow-hidden" ref={ref}>
      {/* Subtle Background Texture */}
      <div className="absolute inset-0 opacity-10">
        <img
          src="https://readdy.ai/api/search-image?query=Tibetan%20thangka%20painting%20texture%20pattern%20mandala%20lotus%20Buddhist%20art%20very%20subtle%20background%20texture%20dark%20warm%20tones%20abstract%20spiritual%20pattern&width=1920&height=1080&seq=thangka_bg001&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-center"
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-16">
        {/* Header */}
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-10 h-px bg-[#c9a96e]"></span>
            <span
              className="text-[#c9a96e] text-[1rem] tracking-[0.3em] font-medium"
              style={{ fontFamily: "'Noto Serif TC', serif" }}
            >
              THANGKA & AMULET
            </span>
            <span className="w-10 h-px bg-[#c9a96e]"></span>
          </div>
          <h2
            className="text-white font-bold mb-5 leading-[1.4]"
            style={{
              fontSize: "clamp(1.5rem, 3vw, 2.125rem)",
              fontFamily: "'Noto Serif TC', serif",
            }}
          >
            唐卡與佛牌｜信仰與藝術的結合
          </h2>
          <p
            className="text-white/60 max-w-2xl mx-auto leading-[1.75]"
            style={{
              fontSize: "1.1875rem",
              fontFamily: "'Noto Serif TC', serif",
            }}
          >
            唐卡與佛牌在藏傳文化中具有深厚的宗教與藝術價值，每一件作品承載著信仰、祝福與文化傳承。
          </p>
        </div>

        {/* Two Column Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-14">
          {/* Card 1 */}
          <div
            className={`rounded-2xl overflow-hidden transition-all duration-1000 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "100ms" }}
          >
            <div className="w-full h-72 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Tibetan%20thangka%20painting%20Buddhist%20art%20colorful%20mandala%20lotus%20deity%20traditional%20artwork%20on%20dark%20background%20luxury%20art%20photography%20warm%20golden%20light%20cultural%20heritage%20collectible&width=700&height=500&seq=thangka001&orientation=landscape"
                alt="唐卡藝術"
                className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
              />
            </div>
            <div className="bg-[#2a2420] p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#c9a96e]/20">
                  <i className="ri-palette-line text-[#c9a96e]"></i>
                </div>
                <span
                  className="text-[#c9a96e] text-[1rem] font-medium"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  文化傳承
                </span>
              </div>
              <h3
                className="text-white font-bold mb-4"
                style={{
                  fontSize: "clamp(1.25rem, 2vw, 1.625rem)",
                  fontFamily: "'Noto Serif TC', serif",
                }}
              >
                唐卡藝術的文化價值
              </h3>
              <p
                className="text-white/60 leading-[1.75]"
                style={{
                  fontSize: "1.1875rem",
                  fontFamily: "'Noto Serif TC', serif",
                }}
              >
                唐卡是藏傳佛教藝術的精髓，每一幅作品都是藝術家以虔誠之心繪製而成，蘊含深厚的宗教意涵與藝術價值，是極具收藏意義的文化珍品。
              </p>
            </div>
          </div>

          {/* Card 2 */}
          <div
            className={`rounded-2xl overflow-hidden transition-all duration-1000 ${
              visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
            style={{ transitionDelay: "250ms" }}
          >
            <div className="w-full h-72 overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Thai%20Buddhist%20amulet%20pendant%20gold%20sacred%20charm%20on%20dark%20velvet%20background%20luxury%20jewelry%20product%20photography%20warm%20golden%20light%20spiritual%20protection%20collectible%20high%20detail&width=700&height=500&seq=thangka002&orientation=landscape"
                alt="佛牌守護"
                className="w-full h-full object-cover object-top transition-transform duration-500 hover:scale-105"
              />
            </div>
            <div className="bg-[#2a2420] p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#c9a96e]/20">
                  <i className="ri-heart-3-line text-[#c9a96e]"></i>
                </div>
                <span
                  className="text-[#c9a96e] text-[1rem] font-medium"
                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                >
                  心靈守護
                </span>
              </div>
              <h3
                className="text-white font-bold mb-4"
                style={{
                  fontSize: "clamp(1.25rem, 2vw, 1.625rem)",
                  fontFamily: "'Noto Serif TC', serif",
                }}
              >
                佛牌的守護力量
              </h3>
              <p
                className="text-white/60 leading-[1.75]"
                style={{
                  fontSize: "1.1875rem",
                  fontFamily: "'Noto Serif TC', serif",
                }}
              >
                每一件佛牌作品承載著信仰、祝福與文化傳承，被許多人視為心靈寄託與守護象徵。在現代生活中，它是連結傳統智慧與當下生命的橋樑。
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div
          className={`text-center transition-all duration-1000 delay-300 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <button
            onClick={() => navigate("/products?category=thangka")}
            className="inline-flex items-center gap-3 px-10 py-4 border border-[#c9a96e] text-[#c9a96e] rounded-full text-[1rem] font-medium tracking-widest cursor-pointer hover:bg-[#c9a96e] hover:text-[#1e1a16] transition-all duration-300 whitespace-nowrap"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            探索唐卡佛牌系列
            <i className="ri-arrow-right-line text-lg"></i>
          </button>
        </div>
      </div>
    </section>
  );
}
